import { Link } from "wouter";
import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/components/language-provider";
import { Search, FileText, Shield, Code, Building2, CreditCard, HelpCircle, MessageCircle } from "lucide-react";
import { WEBSITE_ROUTES } from "@/lib/config";
import { AnimatedSection } from "@/components/landing/animations";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PageHeader } from "@/components/page-header";

export default function Help() {
  const { language } = useLanguage();
  const [search, setSearch] = useState("");

  const categories = [
    {
      icon: FileText,
      title: language === "pt" ? "Documentos" : "Documents",
      items: [
        {
          q: language === "pt" ? "Como enviar um documento para assinatura?" : "How to send a document for signing?",
          a: language === "pt"
            ? "Existem duas formas principais: faça upload direto de um PDF, DOCX ou imagem, ou utilize templates criados no Dynamics 365 CRM que preenchem automaticamente os dados do registro. Além disso, o FillSign oferece o recurso de Coleta de Dados no Documento — onde o signatário preenche campos diretamente no documento durante a assinatura, e esses dados são populados automaticamente de volta na entidade do Dynamics 365. A IA identifica os campos de assinatura automaticamente, agilizando todo o processo."
            : "There are two main ways: upload a PDF, DOCX, or image directly, or use templates created in Dynamics 365 CRM that automatically populate record data. Additionally, FillSign offers a Document Data Collection feature — where the signer fills in fields directly in the document during signing, and that data is automatically populated back into the Dynamics 365 entity. AI identifies signature fields automatically, speeding up the entire process.",
        },
        { q: language === "pt" ? "Quais formatos são aceitos?" : "What formats are accepted?", a: language === "pt" ? "Aceitamos PDF, DOCX, DOC e imagens (JPG, PNG). O documento será convertido para PDF automaticamente." : "We accept PDF, DOCX, DOC and images (JPG, PNG). The document will be automatically converted to PDF." },
        { q: language === "pt" ? "Posso usar templates?" : "Can I use templates?", a: language === "pt" ? "Sim, crie templates reutilizáveis com campos pré-definidos para agilizar o envio. No Dynamics 365, os templates podem ser populados automaticamente com dados do CRM." : "Yes, create reusable templates with pre-defined fields to speed up sending. In Dynamics 365, templates can be automatically populated with CRM data." },
      ],
    },
    {
      icon: Shield,
      title: language === "pt" ? "Segurança e Validade" : "Security & Validity",
      items: [
        { q: language === "pt" ? "As assinaturas têm validade jurídica?" : "Are signatures legally valid?", a: language === "pt" ? "Sim, conforme a MP 2.200-2/2001 e a Lei 14.063/2020. Todas as assinaturas incluem certificado digital e trilha de auditoria." : "Yes, according to MP 2.200-2/2001 and Law 14.063/2020. All signatures include digital certificate and audit trail." },
        { q: language === "pt" ? "Como funciona a autenticação GOV.BR?" : "How does GOV.BR authentication work?", a: language === "pt" ? "O signatário é redirecionado para o portal GOV.BR para autenticação. Após validar a identidade, a assinatura é aplicada automaticamente." : "The signer is redirected to the GOV.BR portal for authentication. After validating identity, the signature is applied automatically." },
      ],
    },
    {
      icon: Building2,
      title: "Dynamics 365",
      items: [
        { q: language === "pt" ? "Como instalar o plugin?" : "How to install the plugin?", a: language === "pt" ? "Baixe a solução, importe no Dynamics 365, configure a API Key e defina a entidade que terá o componente de assinatura. Todo o processo leva menos de 5 minutos." : "Download the solution, import into Dynamics 365, configure the API Key, and define the entity that will have the signature component. The entire process takes less than 5 minutes." },
        { q: language === "pt" ? "É compatível com On-Premises?" : "Is it compatible with On-Premises?", a: language === "pt" ? "Sim, suportamos Dynamics CRM 2016 On-Premises e Dynamics 365 Online." : "Yes, we support Dynamics CRM 2016 On-Premises and Dynamics 365 Online." },
      ],
    },
    {
      icon: Code,
      title: "API",
      items: [
        { q: language === "pt" ? "Como obter as credenciais da API?" : "How to get API credentials?", a: language === "pt" ? "Acesse seu painel, vá em Configurações > API e gere sua API Key. A API Key da REST API é independente da API Key do plugin Dynamics 365." : "Go to your dashboard, navigate to Settings > API and generate your API Key. The REST API Key is independent from the Dynamics 365 plugin API Key." },
        { q: language === "pt" ? "Existe rate limit?" : "Is there a rate limit?", a: language === "pt" ? "Sim, o limite padrão é de 100 requisições por minuto. Planos Enterprise têm limites personalizáveis." : "Yes, the default limit is 100 requests per minute. Enterprise plans have customizable limits." },
      ],
    },
    {
      icon: CreditCard,
      title: language === "pt" ? "Planos e Pagamento" : "Plans & Payment",
      items: [
        { q: language === "pt" ? "Posso cancelar a qualquer momento?" : "Can I cancel anytime?", a: language === "pt" ? "Sim, sem multas ou taxas de cancelamento. Seu plano fica ativo até o fim do período pago." : "Yes, no penalties or cancellation fees. Your plan stays active until the end of the paid period." },
        { q: language === "pt" ? "Quais formas de pagamento?" : "What payment methods?", a: language === "pt" ? "Cartão de crédito, boleto e PIX." : "Credit card, bank transfer, and PIX." },
      ],
    },
  ];

  const filteredCategories = useMemo(() => {
    if (!search) return categories;
    const s = search.toLowerCase();
    return categories.map((cat) => ({
      ...cat,
      items: cat.items.filter((item) => item.q.toLowerCase().includes(s) || item.a.toLowerCase().includes(s)),
    })).filter((cat) => cat.items.length > 0);
  }, [search, categories]);

  return (
    <div className="min-h-screen bg-background">
      <PageHeader />

      <section className="py-16 sm:py-20 md:py-28 blob-bg">
        <div className="container mx-auto px-4 text-center">
          <AnimatedSection>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{language === "pt" ? "Central de Ajuda" : "Help Center"}</h1>
            <p className="text-muted-foreground mb-8 max-w-lg mx-auto">
              {language === "pt" ? "Encontre respostas para suas dúvidas" : "Find answers to your questions"}
            </p>
            <div className="max-w-xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="text"
                className="w-full pl-12 pr-4 py-3.5 rounded-full border bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 text-sm"
                placeholder={language === "pt" ? "Buscar ajuda..." : "Search help..."}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="pb-20">
        <div className="container mx-auto px-4 max-w-4xl">
          {filteredCategories.map((cat, ci) => {
            const Icon = cat.icon;
            return (
              <AnimatedSection key={ci} delay={ci * 0.05} className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "hsla(95, 40%, 38%, 0.08)", color: "var(--fs-forest)" }}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <h2 className="text-xl font-bold">{cat.title}</h2>
                </div>
                <Accordion type="single" collapsible className="space-y-2">
                  {cat.items.map((item, i) => (
                    <AccordionItem key={i} value={`${ci}-${i}`} className="border rounded-xl px-5 bg-card">
                      <AccordionTrigger className="hover:no-underline py-3.5"><span className="text-left text-sm font-medium">{item.q}</span></AccordionTrigger>
                      <AccordionContent className="pb-3.5 text-sm text-muted-foreground leading-relaxed">{item.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </AnimatedSection>
            );
          })}

          {filteredCategories.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <HelpCircle className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>{language === "pt" ? "Nenhum resultado encontrado." : "No results found."}</p>
            </div>
          )}

          <AnimatedSection className="mt-12">
            <div className="card-organic p-6 text-center">
              <p className="font-medium mb-2">{language === "pt" ? "Não encontrou o que procura?" : "Can't find what you're looking for?"}</p>
              <p className="text-sm text-muted-foreground mb-4">{language === "pt" ? "Nossa equipe está pronta para ajudar." : "Our team is ready to help."}</p>
              <Link href={WEBSITE_ROUTES.enterpriseContact}>
                <Button variant="outline" className="rounded-full gap-2"><MessageCircle className="w-4 h-4" />{language === "pt" ? "Falar com suporte" : "Contact support"}</Button>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <footer className="py-8 border-t text-center text-sm text-muted-foreground">
        <div className="container mx-auto px-4">© {new Date().getFullYear()} FillSign.</div>
      </footer>
    </div>
  );
}
